import React, { useState } from "react";
import {
    MapPin, Loader2, RefreshCw, CheckCircle2, PlayCircle, Info,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * City backfill — normalize abbreviations (LA, NYC, SF, …) to canonical
 * "City, ST" form on every existing passport's ``resume_uploads.city``.
 *
 * Same DataPurge-style pattern: dry-run TRUE by default; commit is
 * gated behind a distinct button (no CONFIRM gate needed here since
 * this operation is REVERSIBLE — the audit row records the old value —
 * and lower blast radius than a delete).
 */
export default function CityBackfill() {
    const [dryRunResult, setDryRunResult] = useState(null);
    const [committed, setCommitted] = useState(null);
    const [busy, setBusy] = useState(false);

    const dryRun = async () => {
        setBusy(true);
        setCommitted(null);
        try {
            const { data } = await api.post("/admin/dev/normalize-cities", { dry_run: true });
            setDryRunResult(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const commit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/dev/normalize-cities", { dry_run: false });
            setCommitted(data);
            setDryRunResult(null);
            toast.success(`Normalized ${data.updated} passport row(s).`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-city-backfill">
            <div className="mb-6">
                <p className="overline">Data hygiene · Search visibility</p>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    City Backfill
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Legacy free-text city entries ({`"LA", "NYC", "SF"`}, etc.) don&apos;t match
                    when employers search by full city name. This tool runs the same
                    normalization dictionary the Passport UI uses over every existing
                    passport&apos;s <code className="font-mono">city</code> field, upgrading
                    the abbreviations to the canonical <code className="font-mono">{`"City, ST"`}</code>
                    {" "}form. Idempotent — safe to re-run.
                </p>
            </div>

            <div className="rounded-2xl border border-sky-500/40 bg-sky-500/[0.06] p-4 mb-6 flex items-start gap-3">
                <Info className="h-5 w-5 text-sky-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                    <strong>Lower blast radius than a delete.</strong> Each rewrite is audited
                    (<code className="font-mono">admin.city.backfill.done</code>) with the old + new value so a
                    misfire is traceable. Talent-facing UX for a normalized city is identical to what they typed.
                </div>
            </div>

            <div className="card-soft p-5 space-y-3">
                <div className="text-sm text-foreground">
                    Preview the diff first, then commit if the sample looks right.
                    The dry-run scans every row in <code className="font-mono">resume_uploads</code> but only reports
                    rows whose <code className="font-mono">city</code> would change.
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={dryRun}
                        disabled={busy}
                        data-testid="city-backfill-dryrun-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-white dark:bg-transparent text-emerald-700 dark:text-emerald-400 px-4 py-1.5 text-sm font-semibold hover:bg-emerald-500/10 disabled:opacity-60"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Preview (dry-run)
                    </button>
                </div>
            </div>

            {dryRunResult && (
                <div data-testid="city-backfill-plan" className="card-soft p-5 mt-6 space-y-3">
                    <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-sky-500" />
                        <h3 className="font-heading font-semibold text-foreground">Dry-run result</h3>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Scanned <strong className="text-foreground">{dryRunResult.scanned}</strong> passport row(s).
                        {" "}<strong data-testid="city-backfill-would-update" className="text-foreground">{dryRunResult.would_update}</strong>
                        {" "}would be normalized:
                    </div>

                    {dryRunResult.sample && dryRunResult.sample.length > 0 ? (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-border">
                                    <th className="py-1.5 font-semibold text-muted-foreground">Email</th>
                                    <th className="py-1.5 font-semibold text-muted-foreground">Before</th>
                                    <th className="py-1.5 font-semibold text-muted-foreground">→</th>
                                    <th className="py-1.5 font-semibold text-muted-foreground">After</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dryRunResult.sample.map((row, i) => (
                                    <tr key={i} data-testid={`city-backfill-sample-row-${i}`} className="border-b border-border/50">
                                        <td className="py-1.5 font-mono text-xs truncate max-w-[240px]">{row.email}</td>
                                        <td className="py-1.5 font-mono text-xs text-rose-600 dark:text-rose-400">{row.from}</td>
                                        <td className="py-1.5 text-muted-foreground">→</td>
                                        <td className="py-1.5 font-mono text-xs text-emerald-700 dark:text-emerald-400">{row.to}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-sm text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
                            <CheckCircle2 size={13} /> Nothing to normalize — every city is already canonical.
                        </div>
                    )}

                    {dryRunResult.would_update > 0 && (
                        <div className="pt-3 border-t border-border">
                            <button
                                onClick={commit}
                                disabled={busy}
                                data-testid="city-backfill-commit-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-1.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
                            >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                                {" "}Normalize {dryRunResult.would_update} row(s)
                            </button>
                        </div>
                    )}
                </div>
            )}

            {committed && (
                <div data-testid="city-backfill-committed" className="card-soft border-emerald-500/40 bg-emerald-500/[0.05] p-5 mt-6 space-y-2">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <h3 className="font-heading font-semibold text-foreground">Backfill complete</h3>
                    </div>
                    <div className="text-sm text-foreground">
                        Scanned <strong>{committed.scanned}</strong> · Updated
                        {" "}<strong data-testid="city-backfill-updated-count">{committed.updated}</strong> row(s).
                    </div>
                    {committed.sample && committed.sample.length > 0 && (
                        <details className="mt-2">
                            <summary className="text-xs text-muted-foreground cursor-pointer">Show sample</summary>
                            <table className="w-full text-sm mt-2">
                                <tbody>
                                    {committed.sample.map((row, i) => (
                                        <tr key={i} className="border-b border-border/50">
                                            <td className="py-1.5 font-mono text-xs truncate max-w-[240px]">{row.email}</td>
                                            <td className="py-1.5 font-mono text-xs">{row.from} → {row.to}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
}
